import CartInertiaController from '@/actions/App/Http/Controllers/CartInertiaController';
import { toNumber } from '@/lib/accounting';
import { get, post } from '@/lib/axios';
import { SessionData } from '@/types/session';
import { usePage } from '@inertiajs/react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

// MARK: TYPES
export interface UserCartItem {
  id: string;
  rowId: string;
  name: string;
  price: number;
  qty: number;
  total: number;
  subtotal: number;
  weight: number;
  options: {
    image?: string;
    event_id?: number;
    date?: string;
    start_date?: string;
    end_date?: string;
    site_code?: string;
    site_mapping_code?: string;
    meta?: string;
    service_code?: string;
    service_mapping_code?: string;
    rate_code?: string;
    rate_mapping_code?: string;
    type_name?: string;
    type_code?: string;
    is_rate_based?: boolean;
    services?: any[];
    reservation_code?: string;
    has_inventory?: string;
    expires_at?: string;
    [key: string]: any;
  };
}

interface UserCartContextType {
  cart: UserCartItem[];
  // addToCart: (payload: AddToUserCartPayload) => Promise<any>;
  addRateToCart: (payload: AddRateToCartPayload) => Promise<any>;
  updateCartItem: (payload: UpdateUserCartItemPayload) => Promise<any>;
  removeFromCart: (payload: RemoveFromUserCartPayload) => Promise<any>;
  lockSlots: (payload: LockSlotPayload) => Promise<any>;
  sessionsData: Record<string, SessionData[]>;
  selectedSessions: Record<string, SessionData>;
  sessionsLoading: boolean;
  setSelectedSessions: React.Dispatch<React.SetStateAction<Record<string, SessionData>>>;
  clearCart: () => Promise<any>;
  getCartContent: () => Promise<any>;
  applyVoucher: (payload: ApplyVoucherPayload) => Promise<any>;
  removeVoucher: () => Promise<any>;
  getTotal: () => number;
  getTotalQuantity: () => number;
  getTotalPrice: () => number;
  total: number;
  count: number;
  isFetching: boolean;
  appliedVoucher: any;
  discount: number;
  matchedRowIds: string[];
}

const INITIAL_STATE: UserCartContextType = {
  cart: [],
  sessionsData: {},
  selectedSessions: {},
  sessionsLoading: false,
  setSelectedSessions: () => {},
  // addToCart: () => Promise.resolve({}),
  addRateToCart: () => Promise.resolve({}),
  updateCartItem: () => Promise.resolve({}),
  removeFromCart: () => Promise.resolve({}),
  clearCart: () => Promise.resolve({}),
  getCartContent: () => Promise.resolve({}),
  applyVoucher: () => Promise.resolve({}),
  lockSlots: () => Promise.resolve({}),
  removeVoucher: () => Promise.resolve({}),
  getTotal: () => 0,
  getTotalQuantity: () => 0,
  getTotalPrice: () => 0,
  total: 0,
  count: 0,
  discount: 0,
  isFetching: false,
  appliedVoucher: null,
  matchedRowIds: [],
};

const USER_CART_ROUTES = {
  get_cart: 'cart/content',
  // add_to_cart: 'cart/add',
  rate_add_to_cart: 'cart/rate-add',
  remove_from_cart: 'cart/remove',
  update_cart_item: 'cart/update',
  clear_cart: 'cart/clear',
  flash_add: 'cart/flash-add',
  flash_update: 'cart/flash-update',
  apply_voucher: 'cart/apply-voucher',
  remove_voucher: 'cart/remove-voucher',
  fetch_sessions: 'cart/sessions',
  lock_slots: 'cart/lock-slots',
};

export interface AddToUserCartPayload {
  id: number;
  name: string;
  price: number;
  quantity: number;
  event_id: number;
  image?: string;
  date?: string;
  start_date?: string;
  end_date?: string;
  type?: 'json';
}

export interface UpdateUserCartItemPayload {
  rowId: string;
  quantity: number;
}

export interface RemoveFromUserCartPayload {
  rowId: string;
}

export interface ApplyVoucherPayload {
  voucher_code: string;
  row_ids: string[];
}

export interface AddRateToCartPayload {
  rate_mapping_code: string;
  site_mapping_code: string;
  date: string;
  services: Array<{
    service_mapping_code: string;
    quantity: number;
  }>;
}

export interface FlashAddToUserCartPayload {
  services: Array<{
    service_id: string;
    service_mapping_code?: string;
    service_name?: string;
    number: number;
    rate_id: number;
    rate_mapping_code?: string;
  }>;
  site: string;
  rate: string;
  total: number;
  date: string;
}

export interface LockSlotPayload {
  services: Array<{
    row_id: string;
    session_id: string | number;
    outlet_name: string;
    start_time: string;
  }>;
}

export interface UserCartResponse {
  cart: Record<string, UserCartItem> | UserCartItem[];
  count: number;
  total: string;
  discount: string;
  success?: boolean;
  message?: string;
}

export interface FetchSessionsResponse {
  data: Record<string, SessionData[]>;
  success?: boolean;
  message?: string;
}

export interface LockSlotsResponse {
  data: {
    reservationCode: string;
  };
  success?: boolean;
  message?: boolean;
}

const convertToUserCartItem = (items: Record<string, UserCartItem> | UserCartItem[]): UserCartItem[] => {
  if (Array.isArray(items)) {
    return items.map((item) => ({
      ...item,
      id: item.id,
      rowId: item.rowId,
      name: item.name,
    }));
  }

  return Object.values(items).map((item) => ({
    ...item,
    id: item.id,
    rowId: item.rowId,
    name: item.name,
    total: item.subtotal || item.price * item.qty,
  }));
};

export type LockStatus = 'unlocked' | 'locked' | 'expired';

// MARK: PROVIDER
export const UserCartProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    cart: propsCart,
    cartCount: propsCartCount,
    cartDiscount: propsCartDiscount,
  } = usePage<{ cart: UserCartItem[]; cartCount: number; cartDiscount: number }>().props;

  const [cart, setCart] = useState<UserCartItem[]>(INITIAL_STATE.cart);
  const [total, setTotal] = useState(INITIAL_STATE.total);
  const [count, setCount] = useState(INITIAL_STATE.count);
  const [isFetching, setIsFetching] = useState(false);
  const [appliedVoucher, setAppliedVoucher] = useState<any>(null);
  const [discount, setDiscount] = useState(0);
  const [matchedRowIds, setMatchedRowIds] = useState<string[]>([]);
  const [sessionsData, setSessionsData] = useState<Record<string, SessionData[]>>({} as Record<string, SessionData[]>);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState<Record<string, SessionData>>({} as Record<string, SessionData>);

  const getCartContent = useCallback(async () => {
    const response = await getCartContentApi();
    if (response?.data?.cart) {
      const cartItems = convertToUserCartItem(response.data.cart);
      setCart(cartItems);
      setTotal(toNumber(response.data.total) || 0);
      setCount(response.data.count || 0);
      setDiscount(toNumber(response.data.discount) || 0);
      return response;
    }
    return response;
  }, []);

  const fetchSessions = useCallback(async () => {
    const response = await fetchSessionsApi();
    if (response?.success && response?.data) {
      setSessionsData(response.data);
    }
  }, []);

  const lockSlots = useCallback(async (payload: LockSlotPayload) => {
    const response = await lockSlotsApi(payload);
    return response;
  }, []);

  // const addToCart = async (payload: AddToUserCartPayload) => {
  //     const response = await addToCartApi(payload);
  //     if (response?.data?.cart?.items) {
  //         const cartItems = convertToUserCartItem(response.data.cart.items);
  //         setCart(cartItems);
  //         setTotal(toNumber(response.data.cart.total) || 0);
  //         setCount(response.data.cart.count || 0);
  //     }
  //     return response;
  // };

  const addRateToCart = async (payload: AddRateToCartPayload) => {
    const response = await addRateToCartApi(payload);
    if (response?.data?.cart?.items) {
      const cartItems = convertToUserCartItem(response.data.cart.items);
      setCart(cartItems);
      setTotal(toNumber(response.data.cart.total) || 0);
      setCount(response.data.cart.count || 0);
    }
    return response;
  };

  const updateCartItem = async (payload: UpdateUserCartItemPayload) => {
    const response = await updateCartItemApi(payload);
    if (response?.data?.cart?.items) {
      const cartItems = convertToUserCartItem(response.data.cart.items);
      setCart(cartItems);
      setTotal(toNumber(response.data.cart.total) || 0);
      setCount(response.data.cart.count || 0);
    }
    return response;
  };

  const removeFromCart = async (payload: RemoveFromUserCartPayload) => {
    const response = await removeFromCartApi(payload);
    if (response?.data?.cart) {
      const cartItems = convertToUserCartItem(response.data.cart.items || response.data.cart);
      setCart(cartItems);
      setTotal(toNumber(response.data.cart.total || response.data.total) || 0);
      setCount(response.data.count || 0);
    } else {
      const newCart = cart.filter((item) => item.rowId !== payload.rowId);
      setCart(newCart);
      setTotal(newCart.reduce((acc, item) => acc + item.total, 0));
      setCount(newCart.length);
    }
    return response;
  };

  const clearCart = async () => {
    const response = await clearCartApi();
    setCart([]);
    setTotal(0);
    setCount(0);
    return response;
  };

  const getTotal = () => {
    return cart.reduce((acc, item) => acc + item.total, 0);
  };

  const getTotalQuantity = () => {
    return cart.reduce((acc, item) => acc + item.qty, 0);
  };

  const getTotalPrice = () => {
    return cart.reduce((acc, item) => acc + item.price * item.qty, 0);
  };

  const applyVoucher = async (payload: ApplyVoucherPayload) => {
    const response = await applyVoucherApi(payload);
    if (response?.success && response?.data?.voucher) {
      setAppliedVoucher(response.data.voucher);
      if (response.data.cart?.matched_row_ids) {
        setMatchedRowIds(response.data.cart.matched_row_ids);
      }
      if (response.data.cart?.items) {
        const cartItems = convertToUserCartItem(response.data.cart.items);
        setCart(cartItems);
        setTotal(toNumber(response.data.cart.total) || 0);
        setCount(response.data.cart.count || 0);
      }
      if (response.data.cart?.discount !== undefined) {
        setDiscount(toNumber(response.data.cart.discount) || 0);
      }
      window.dispatchEvent(new Event('cart-updated'));
    }
    return response;
  };

  const removeVoucher = async () => {
    const response = await removeVoucherApi();
    if (response?.success) {
      setAppliedVoucher(null);
      setMatchedRowIds([]);
      setDiscount(0);
      if (response.data?.cart?.items) {
        const cartItems = convertToUserCartItem(response.data.cart.items);
        setCart(cartItems);
        setTotal(toNumber(response.data.cart.total) || 0);
        setCount(response.data.cart.count || 0);
      }
      window.dispatchEvent(new Event('cart-updated'));
    }
    return response;
  };

  // MARK: API
  // const addToCartApi = async (payload: AddToUserCartPayload) => {
  //     setIsFetching(true);
  //     return await post(USER_CART_ROUTES.add_to_cart, payload).finally(() => setIsFetching(false));
  // };

  const getCartContentApi = async () => {
    setIsFetching(true);
    return await get<UserCartResponse>(USER_CART_ROUTES.get_cart).finally(() => setIsFetching(false));
  };

  const fetchSessionsApi = async () => {
    setSessionsLoading(true);
    return await get<FetchSessionsResponse>(USER_CART_ROUTES.fetch_sessions).finally(() => setSessionsLoading(false));
  };

  const lockSlotsApi = async (payload: LockSlotPayload) => {
    setIsFetching(true);
    return await post<LockSlotsResponse>(USER_CART_ROUTES.lock_slots, payload).finally(() => setIsFetching(false));
  };

  const removeFromCartApi = async (payload: RemoveFromUserCartPayload) => {
    setIsFetching(true);
    const url = CartInertiaController.remove({ rowId: payload.rowId }).url;
    return await post(url, payload).finally(() => setIsFetching(false));
  };

  const addRateToCartApi = async (payload: AddRateToCartPayload) => {
    setIsFetching(true);
    return await post(USER_CART_ROUTES.rate_add_to_cart, payload).finally(() => setIsFetching(false));
  };

  const updateCartItemApi = async (payload: UpdateUserCartItemPayload) => {
    setIsFetching(true);
    return await post(USER_CART_ROUTES.flash_update, payload).finally(() => setIsFetching(false));
  };

  const clearCartApi = async () => {
    setIsFetching(true);
    return await post(USER_CART_ROUTES.clear_cart).finally(() => setIsFetching(false));
  };

  const applyVoucherApi = async (payload: ApplyVoucherPayload) => {
    setIsFetching(true);
    return await post(USER_CART_ROUTES.apply_voucher, payload).finally(() => setIsFetching(false));
  };

  const removeVoucherApi = async () => {
    setIsFetching(true);
    return await post(USER_CART_ROUTES.remove_voucher).finally(() => setIsFetching(false));
  };

  // Removed unused flashAddApi function

  // Listen for cart-updated events
  useEffect(() => {
    const handleCartUpdated = () => {
      getCartContent();
    };

    window.addEventListener('cart-updated', handleCartUpdated);
    // Initial cart load
    if (propsCart) {
      const cartItems = convertToUserCartItem(propsCart);
      setCart(cartItems);
      setCount(propsCartCount);
      setDiscount(toNumber(propsCartDiscount) || 0);
      setTotal(cartItems.reduce((acc, item) => acc + item.total, 0));
    } else {
      getCartContent();
    }

    return () => {
      window.removeEventListener('cart-updated', handleCartUpdated);
    };
  }, [getCartContent]);

  useEffect(() => {
    // check is cart page
    if (window.location.pathname === '/cart' && cart && cart.length > 0 && cart.some((item) => item.options.has_inventory)) {
      fetchSessions();
    }
  }, [cart, fetchSessions]);

  return (
    <UserCartContext.Provider
      value={{
        lockSlots,
        cart,
        sessionsData,
        selectedSessions,
        addRateToCart,
        sessionsLoading,
        setSelectedSessions,
        removeFromCart,
        clearCart,
        getCartContent,
        getTotal,
        getTotalQuantity,
        getTotalPrice,
        total,
        count,
        updateCartItem,
        isFetching,
        applyVoucher,
        removeVoucher,
        appliedVoucher,
        discount,
        matchedRowIds,
      }}
    >
      {children}
    </UserCartContext.Provider>
  );
};

// MARK: CONTEXT
const UserCartContext = createContext<UserCartContextType | undefined>(INITIAL_STATE);

export function useUserGlobalCart() {
  const context = useContext(UserCartContext);
  if (!context) {
    throw new Error('useUserGlobalCart must be used within a UserCartProvider');
  }
  return context;
}
